import { useEffect, useState } from "react";
import { CheckCircle2, Info, Settings2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useCurrencies, useProfile } from "@/hooks";
import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { queryClient } from "@/services/queryClient";
import { toast } from "sonner";
import type { MerchantSettings as MerchantSettingsType } from "@/interfaces/admin-setting";

interface MerchantSettingsProps {
  userId: string;
}

const MerchantSetting = ({ userId }: MerchantSettingsProps) => {
  const { data: profile } = useProfile(userId);
  const { data: currencies } = useCurrencies();

  const [loading, setLoading] = useState(false);

  const [merchantSettings, setMerchantSettings] =
    useState<MerchantSettingsType>({
      merchantName: "",
      currencyId: "",
      floatingMargin: 2.5,
      autoCancelMinutes: 15,
      requireKyc: true,
      requirePhoneVerification: true,
      autoReplyEnabled: true,
      autoReplyMessage: "Welcome. Please follow trade instructions carefully.",
      tradeNotifications: true,
      hideOfflineAds: true,
      maxActiveAds: 10,
    });

  useEffect(() => {
    if (!profile) return;

    setMerchantSettings({
      merchantName: profile.merchantName || "",
      currencyId: profile.setting?.currency?.id || "",
      floatingMargin: profile.setting?.floatingMargin ?? 0,
      autoCancelMinutes: profile.setting?.autoCancelMinutes ?? 15,
      autoReplyMessage: profile.setting?.autoReplyMessage || "",

      requireKyc: true,
      requirePhoneVerification: true,
      autoReplyEnabled: true,
      tradeNotifications: true,
      hideOfflineAds: true,
      maxActiveAds: 10,
    });
  }, [profile]);

  const handleUpdate = async () => {
    const payload = {
      merchantName: merchantSettings.merchantName,
      currencyId: merchantSettings.currencyId,
      floatingMargin: merchantSettings.floatingMargin,
      autoCancelMinutes: merchantSettings.autoCancelMinutes,
      autoReplyMessage: merchantSettings.autoReplyMessage,
    };

    setLoading(true);

    try {
      const res = await makeRequest.put("/user/update-merchant/me", payload);

      toast.success(
        res.data.message || "Merchant settings updated successfully",
      );

      await queryClient.invalidateQueries({
        queryKey: ["profile", userId],
      });
    } catch (error) {
      toast.error(
        getErrorMessage(error) || "An error occurred while updating settings",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border bg-card p-6">
      <div className="flex justify-between mb-8">
        <div>
          <h3 className="font-semibold text-lg">Merchant Settings</h3>

          <p className="text-sm text-muted-foreground">
            Configure your P2P trading experience
          </p>
        </div>

        <Settings2 className="text-primary" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Merchant Nickname</Label>

            <Input
              value={merchantSettings.merchantName}
              placeholder="Enter merchant name"
              onChange={(e) =>
                setMerchantSettings((prev) => ({
                  ...prev,
                  merchantName: e.target.value,
                }))
              }
              className="h-14"
            />
          </div>

          <div className="space-y-2">
            <Label>Default Fiat Currency</Label>

            <Select
              value={merchantSettings.currencyId}
              onValueChange={(value) =>
                setMerchantSettings((prev) => ({
                  ...prev,
                  currencyId: value,
                }))
              }
            >
              <SelectTrigger className="!h-12 w-full">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>

              <SelectContent>
                {currencies?.map((currency) => (
                  <SelectItem key={currency.id} value={currency.id}>
                    {currency.iso} ({currency.symbol})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Order Auto Cancel (Minutes)</Label>

            <Input
              type="number"
              value={merchantSettings.autoCancelMinutes}
              onChange={(e) =>
                setMerchantSettings((prev) => ({
                  ...prev,
                  autoCancelMinutes: Number(e.target.value),
                }))
              }
              className="h-14"
            />
          </div>

          <div className="space-y-2">
            <Label>Floating Margin (%)</Label>

            <Input
              type="number"
              value={merchantSettings.floatingMargin}
              onChange={(e) =>
                setMerchantSettings((prev) => ({
                  ...prev,
                  floatingMargin: Number(e.target.value),
                }))
              }
              className="h-14"
            />
          </div>

          <div className="space-y-2">
            <Label>Auto Reply Message</Label>

            <Textarea
              rows={5}
              value={merchantSettings.autoReplyMessage}
              onChange={(e) =>
                setMerchantSettings((prev) => ({
                  ...prev,
                  autoReplyMessage: e.target.value,
                }))
              }
            />
          </div>

          <div className="flex justify-end">
            <Button disabled={loading} onClick={handleUpdate}>
              {loading ? "Updating..." : "Update Settings"}
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-4">
          <div className="flex justify-between mb-6">
            <div>
              <h3 className="font-semibold text-lg">
                Ad Creation Requirements
              </h3>

              <p className="text-sm text-muted-foreground">
                Merchant eligibility checklist
              </p>
            </div>

            <Info className="text-primary" />
          </div>

          <div className="space-y-4">
            {[
              "Complete identity verification (KYC)",
              "Link at least one verified payment account",
              "Enable 2FA for account protection",
              "Maintain sufficient wallet balance",
              "Accept platform P2P merchant terms",
              "Set payment timeout window",
              "Configure trade auto-reply",
              "Choose supported fiat currency",
              "Provide dispute handling terms",
              "Configure min/max order limits",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center justify-between border rounded-xl p-4"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="text-emerald-500 w-5 h-5" />
                  <span className="text-sm">{item}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantSetting;
